import { parseLocalePath } from './lang-utils';

/**
 * Rewrites an absolute page URL onto a locale segment.
 *
 * Returns the URL untouched if it cannot be parsed, so a relative or malformed
 * value degrades to the previous behaviour rather than throwing during render.
 */
const withLocaleSegment = (url, locale) => {
  try {
    const parsed = new URL(url);

    parsed.pathname = `/${locale}`;

    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * The canonical URL for the page currently being rendered.
 *
 * Sitecore supplies `url` as the item's absolute URL, which for the home item is
 * always the bare root: the Layout Service is asked for route `/` plus a
 * language, so /us and / arrive identically and the resolver cannot tell them
 * apart. That is why the canonical read as the root on both market home pages.
 * The locale is only knowable from the path, so it is reapplied here.
 *
 * Scoped to the market home pages on purpose. Deep pages resolve under a locale
 * prefix too (/us/about/x returns 200), but nothing links to them that way, and
 * canonicalising those to the prefixed form would promote three URLs for one
 * page rather than collapsing them onto the unprefixed one.
 *
 * @param {string} url      absolute URL of the item, from Sitecore
 * @param {string} pathname current path, e.g. '/us' or '/about/careers'
 * @returns {string}
 */
export const resolveCanonicalUrl = (url, pathname = '') => {
  const { locale, rest } = parseLocalePath(pathname);
  const isMarketHome = !!locale && (rest === '' || rest === '/');

  return isMarketHome ? withLocaleSegment(url, locale) : url;
};
